import { useEffect, useRef } from 'react';

import { trackingBehaviorUserService } from 'services/assignments';
import { removeLocalStorage } from 'services/common/storage';
import {
  PROCESS_DELAY,
  TRACKING_STEP_QUEUE,
  TRACKING_STEP_QUEUE_FAIL
} from 'utils/constants';

const useOfflineTracking = () => {
  const timeoutRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const isProcessingRef = useRef(false);

  const getQueueFail = () => {
    try {
      const dataFail = localStorage.getItem(TRACKING_STEP_QUEUE_FAIL);
      return dataFail ? JSON.parse(dataFail) as any[] : [];
    } catch {
      removeLocalStorage(TRACKING_STEP_QUEUE_FAIL);
      return [];
    }
  };

  const getQueue = () => {
    try {
      const data = localStorage.getItem(TRACKING_STEP_QUEUE);
      const dataFail = getQueueFail();
      return [...(data ? JSON.parse(data) as any[] : []), ...dataFail];
    } catch {
      removeLocalStorage(TRACKING_STEP_QUEUE);
      return [];
    }
  };

  const setQueue = (queue: any[]) => {
    localStorage.setItem(TRACKING_STEP_QUEUE, JSON.stringify(queue));
  };

  const processNext = async () => {
    if (isProcessingRef.current) return;

    if (!navigator.onLine) {
      timeoutRef.current = setTimeout(processNext, PROCESS_DELAY);
      return;
    }

    const queue = getQueue();

    if (!queue.length) return;

    const item = queue[0];
    isProcessingRef.current = true;

    try {
      await trackingBehaviorUserService(item);

      // remove item nếu thành công
      queue.shift();
      setQueue(queue);
    } catch (error: any) {
      if (error?.response?.status === 429) {
        // nếu bị 429 → delay 60s
        timeoutRef.current = setTimeout(processNext, 60000);
        isProcessingRef.current = false;
        return;
      }
      if (error?.response?.status === 422) {
        // nếu bị 422 → remove item đó khỏi queue
        queue.shift();
        setQueue(queue);
        isProcessingRef.current = false;
        timeoutRef.current = setTimeout(processNext, PROCESS_DELAY);
        return;
      }
    }

    isProcessingRef.current = false;
    timeoutRef.current = setTimeout(processNext, PROCESS_DELAY);
  };

  useEffect(() => {
    processNext();
    window.addEventListener('online', processNext);

    return () => {
      window.removeEventListener('online', processNext);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);
};

export default useOfflineTracking;
